// src/pages/Users.js
import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from '../Styles/Users.module.css';
import IconUsers from '../Images/IconUsers';
import IconButton from '../Images/IconButton';
import AccountIcon from '../Components/AccountIcon';
import { useUsers } from '../Commoncontext/UsersContext';
import { useSelectedCompany } from '../Commoncontext/SelectedCompanyContext';
import { useToast } from '../Commoncontext/ToastContext';

/* ---------- Helpers ---------- */
const fmtDate = (ts) => {
  if (!ts) return '-';
  if (ts?._seconds) return new Date(ts._seconds * 1000).toLocaleDateString('fi-FI');
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('fi-FI');
};

const ROLE_ORDER = ['asukas', 'osakas', 'hallitus', 'isännöitsijä', 'admin'];
const ROLE_LABEL = { asukas: 'Asukas', osakas: 'Osakas', hallitus: 'Hallitus', 'isännöitsijä': 'Isännöitsijä', admin: 'Admin' };
function formatRoles(roles) {
  if (!Array.isArray(roles) || roles.length === 0) return '-';
  const uniq = Array.from(new Set(roles.map((r) => String(r).toLowerCase())));
  const sorted = uniq.sort((a, b) => ROLE_ORDER.indexOf(a) - ROLE_ORDER.indexOf(b));
  return sorted.map((r) => ROLE_LABEL[r] || r).join(', ');
}

/* ---------- Validation ---------- */
const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const validateUserForm = (f) => {
  const e = {};
  if (!f.firstName?.trim()) e.firstName = 'Etunimi on pakollinen.';
  if (!f.lastName?.trim()) e.lastName = 'Sukunimi on pakollinen.';
  if (!f.email?.trim()) e.email = 'Sähköposti on pakollinen.';
  else if (!emailRe.test(f.email.trim())) e.email = 'Sähköpostin muoto on virheellinen.';
  if (!f.password || f.password.length < 6) e.password = 'Salasanan tulee olla vähintään 6 merkkiä.';
  if (!Array.isArray(f.roles) || f.roles.length === 0) e.roles = 'Valitse vähintään yksi rooli.';
  return e;
};
const validateInviteForm = (f) => {
  const e = {};
  if (!f.firstName?.trim()) e.firstName = 'Etunimi on pakollinen.';
  if (!f.lastName?.trim()) e.lastName = 'Sukunimi on pakollinen.';
  if (!f.email?.trim()) e.email = 'Sähköposti on pakollinen.';
  else if (!emailRe.test(f.email.trim())) e.email = 'Sähköpostin muoto on virheellinen.';
  if (!Array.isArray(f.roles) || f.roles.length === 0) e.roles = 'Valitse vähintään yksi rooli.';
  return e;
};

export default function Users() {
  const { selectedCompanyId } = useSelectedCompany();
  const {
    users, loading, fetchUsers, updateUserActive, sendPasswordReset,
    invites, invitesLoading, fetchInvites, createInvite, resendInvite, cancelInvite,
    createUser,
  } = useUsers();

  const navigate = useNavigate();
  const { showToast } = useToast();

  /* ---------- Tabs ---------- */
  const [tab, setTab] = useState('users'); // 'users' | 'invites'

  /* ---------- USERS list & pagination ---------- */
  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 9;

  const sortedUsers = useMemo(() => {
    const filtered = (users || []).filter(u => !(u.roolit?.includes('admin') && (u.roolit?.length === 1)));
    return filtered.sort((a, b) => (a.sukunimi || '').localeCompare(b.sukunimi || ''));
  }, [users]);

  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = sortedUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(sortedUsers.length / usersPerPage);

  const handleNextPage = () => { if (currentPage < totalPages) setCurrentPage(p => p + 1); };
  const handlePreviousPage = () => { if (currentPage > 1) setCurrentPage(p => p - 1); };

  /* ---------- INVITES: client-side filter + pagination ---------- */
  const [inviteFilter, setInviteFilter] = useState('pending'); // 'pending' | 'accepted' | 'expired' | 'all'
  const [invitePage, setInvitePage] = useState(1);
  const invitesPerPage = 9;

  // Filter in UI (no extra backend calls on pill change)
  const filteredInvites = useMemo(() => {
    if (!invites) return [];
    if (inviteFilter === 'all') return invites;
    return invites.filter(iv => (iv.status || 'pending') === inviteFilter);
  }, [invites, inviteFilter]);

  useEffect(() => { setInvitePage(1); }, [inviteFilter]);

  const inviteIndexLast = invitePage * invitesPerPage;
  const inviteIndexFirst = inviteIndexLast - invitesPerPage;
  const currentInvites = filteredInvites.slice(inviteIndexFirst, inviteIndexLast);
  const totalInvitePages = Math.ceil(filteredInvites.length / invitesPerPage);

  const handleInviteNextPage = () => { if (invitePage < totalInvitePages) setInvitePage(p => p + 1); };
  const handleInvitePrevPage = () => { if (invitePage > 1) setInvitePage(p => p - 1); };

  /* ---------- Create modal state ---------- */
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createMode, setCreateMode] = useState('user'); // 'user' | 'invite'
  const [createForm, setCreateForm] = useState({
    firstName: '', lastName: '', email: '', password: '', roles: ['asukas'],
  });
  const [touched, setTouched] = useState({ firstName: false, lastName: false, email: false, password: false, roles: false });
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState('');

  // Refs for focusing first error
  const firstNameRef = useRef(null);
  const lastNameRef = useRef(null);
  const emailRef = useRef(null);
  const passwordRef = useRef(null);
  const rolesGroupRef = useRef(null);

  /* ---------- Data fetch ---------- */
  useEffect(() => {
    if (!selectedCompanyId) return;
    fetchUsers(selectedCompanyId);
  }, [selectedCompanyId, fetchUsers]);

  // Fetch invites ONCE per company change. Filter & paginate in UI.
  useEffect(() => {
    if (!selectedCompanyId) return;
    fetchInvites(selectedCompanyId);
  }, [selectedCompanyId, fetchInvites]);

  /* ---------- Handlers ---------- */
  const handleRowClick = (userId) => navigate(`/owninformation/${userId}`);

  const handleToggleActive = async (e, userId, currentActive, name) => {
    e.stopPropagation();
    try {
      await updateUserActive(userId, !currentActive);
      showToast(`Käyttäjä ${name} ${!currentActive ? 'aktivoitu' : 'inaktivoitu'}`, 'success');
    } catch {
      showToast('Käyttäjän aktivointi epäonnistui. Yritä uudelleen.', 'error');
    }
  };

  const handlePasswordReset = async (e, email, etunimi, sukunimi) => {
    e.stopPropagation();
    try {
      const result = await sendPasswordReset(email);
      showToast(
        result ? `Käyttäjälle ${etunimi} ${sukunimi} lähetetty resetointilinkki`
               : 'Salanan resetointi epäonnistui. Yritä uudelleen.',
        result ? 'success' : 'error'
      );
    } catch {
      showToast('Salanan resetointi epäonnistui. Yritä uudelleen.', 'error');
    }
  };

  const handleResendInvite = async (inviteId) => {
    try {
      await resendInvite(inviteId);
      showToast('Kutsu uudelleen lähetetty käyttäjälle.', 'success');
      // ei refetchiä — aikaleimat voi päivittää tarvittaessa myöhemmin
    } catch (e) {
      console.error('resendInvite error:', e);
      showToast('Kutsun uudelleenlähetys epäonnistui.', 'error');
    }
  };

  const handleCancelInvite = async (inviteId) => {
    try {
      await cancelInvite(inviteId); // context poistaa rivin muistista
      showToast('Kutsu poistettu onnistuneesti.', 'success');
    } catch (e) {
      console.error('cancelInvite error:', e);
      showToast('Kutsun poistaminen epäonnistui.', 'error');
    }
  };

  /* ---------- Create modal helpers ---------- */
  const resetFormState = (mode) => {
    setCreateMode(mode);
    setCreateForm({ firstName: '', lastName: '', email: '', password: '', roles: ['asukas'] });
    setTouched({ firstName: false, lastName: false, email: false, password: false, roles: false });
    setSubmitAttempted(false);
    setToast('');
  };
  const openCreateUser = () => { resetFormState('user'); setShowCreateModal(true); };
  const openCreateInvite = () => { resetFormState('invite'); setShowCreateModal(true); };
  const closeCreateModal = useCallback(() => { setShowCreateModal(false); setToast(''); }, []);

  // Escape sulkee
  useEffect(() => {
    if (!showCreateModal) return;
    const onKeyDown = (e) => { if (e.key === 'Escape') closeCreateModal(); };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [showCreateModal, closeCreateModal]);

  // Autofocus etunimi
  useEffect(() => { if (showCreateModal) setTimeout(() => firstNameRef.current?.focus(), 0); }, [showCreateModal]);

  const toggleRole = (role) => {
    setCreateForm((prev) => {
      const has = prev.roles.includes(role);
      const nextRoles = has ? prev.roles.filter((r) => r !== role) : [...prev.roles, role];
      return { ...prev, roles: nextRoles };
    });
    setTouched((t) => ({ ...t, roles: true }));
  };

  const formErrors = useMemo(() => (createMode === 'user' ? validateUserForm(createForm) : validateInviteForm(createForm)), [createForm, createMode]);
  const showError = (key) => (formErrors[key] && (touched[key] || submitAttempted));
  const disableSave = busy;

  const focusFirstError = () => {
    const orderUser = ['firstName', 'lastName', 'email', 'password', 'roles'];
    const orderInvite = ['firstName', 'lastName', 'email', 'roles'];
    const order = createMode === 'user' ? orderUser : orderInvite;
    const firstKey = order.find((k) => formErrors[k]);
    if (!firstKey) return;
    switch (firstKey) {
      case 'firstName': firstNameRef.current?.focus(); break;
      case 'lastName': lastNameRef.current?.focus(); break;
      case 'email': emailRef.current?.focus(); break;
      case 'password': passwordRef.current?.focus(); break;
      case 'roles':
        if (rolesGroupRef.current) { rolesGroupRef.current.setAttribute('tabindex', '-1'); rolesGroupRef.current.focus(); }
        break;
      default: break;
    }
  };

  const onSubmitCreate = async () => {
    if (!selectedCompanyId) return;
    setSubmitAttempted(true);
    if (Object.keys(formErrors).length > 0) { focusFirstError(); return; }
    setBusy(true);
    try {
      const f = createForm;
      if (createMode === 'user') {
        await createUser(selectedCompanyId, {
          firstName: f.firstName.trim(),
          lastName: f.lastName.trim(),
          email: f.email.trim().toLowerCase(),
          password: f.password,
          roles: f.roles,
          aktiivinen: true,
        });
        showToast('Käyttäjä luotu onnistuneesti.', 'success');
      } else {
        await createInvite(selectedCompanyId, {
          name: `${f.firstName.trim()} ${f.lastName.trim()}`,
          email: f.email.trim().toLowerCase(),
          roles: f.roles,
        });
        showToast('Kutsu luotu ja lähetetty.', 'success');
        // ei refetchiä; lista pysyy muistissa
      }
      closeCreateModal();
    } catch (err) {
      if (err?.code === 'email_in_use' || err?.httpStatus === 409) {
        showToast('Sähköpostiosoite on jo käytössä.', 'error');
      } else if (err?.code === 'weak_password') {
        showToast('Salasanan tulee olla vähintään 6 merkkiä.', 'error');
      } else {
        showToast('Käyttäjän/kutsun luonti epäonnistui. Yritä uudelleen.', 'error');
      }
    } finally {
      setBusy(false);
    }
  };

  const onBlurField = (key) => setTouched((t) => ({ ...t, [key]: true }));
  const onChangeField = (key, value) => setCreateForm((p) => ({ ...p, [key]: value }));

  /* ---------- Render ---------- */
  return (
    <div className={styles.page}>
      <div className={styles.content}>
        <div className={styles.pageTitle}>
          <div className={styles.titleIcon}><IconUsers /></div>
          <h1>Käyttäjähallinta</h1>
        </div>

        {/* Tabs + primary */}
        <div className={styles.tabsRow}>
          <button className={`${styles.tab} ${tab === 'users' ? styles.tabActive : ''}`} onClick={() => setTab('users')}>
            Käyttäjät
          </button>
          <button className={`${styles.tab} ${tab === 'invites' ? styles.tabActive : ''}`} onClick={() => setTab('invites')}>
            Kutsut
          </button>
          <div className={styles.mlAuto}>
            {tab === 'users'
              ? <button className={styles.primaryBtn} onClick={openCreateUser}>+ Uusi käyttäjä</button>
              : <button className={styles.primaryBtn} onClick={openCreateInvite}>+ Uusi kutsu</button>}
          </div>
        </div>

        {/* USERS TAB */}
        {tab === 'users' && (
          <div className={styles.userlistCard}>
            <table className={styles.userTable}>
              <thead className={styles.tableHead}>
                <tr>
                  <th className={styles.colName}>Nimi</th>
                  <th className={styles.colEmail}>Sähköposti</th>
                  <th className={styles.colLogin}>Kirjautunut</th>
                  <th className={styles.colRoles}>Roolit</th>
                  <th className={styles.colState}>Tila</th>
                  <th className={styles.colActions}>Toiminnot</th>
                </tr>
              </thead>
              <tbody className={styles.tableBody}>
                {loading && (<tr><td colSpan={6} className={styles.muted}>Ladataan…</td></tr>)}
                {!loading && currentUsers.map((u) => {
                  const last = fmtDate(u.kirjautunut);
                  const active = !!u.aktiivinen;
                  return (
                    <tr key={u.id} onClick={() => handleRowClick(u.id)} className={styles.memberRow}>
                      <td>
                        <div className={styles.nameCell}>
                          <AccountIcon profileImageUrl={u.profiilikuvaurl} width="32px" height="32px" border />
                          <span>{u.etunimi} {u.sukunimi}</span>
                        </div>
                      </td>
                      <td>{u.sähköposti}</td>
                      <td>{last}</td>
                      <td>{formatRoles(u.roolit)}</td>
                      <td>
                        {active
                          ? <button className={styles.statusActiveBtn}>Aktiivinen</button>
                          : <button className={styles.statusInactiveBtn}>Inaktiivinen</button>}
                      </td>
                      <td>
                        <div className={styles.actions}>
                          <button
                            className={styles.stateButton}
                            onClick={(e) => handleToggleActive(e, u.id, u.aktiivinen, `${u.etunimi} ${u.sukunimi}`)}
                          >
                            {active ? 'Inaktivoi' : 'Aktivoi'}
                          </button>
                          <button
                            className={styles.stateButton}
                            onClick={(e) => handlePasswordReset(e, u.sähköposti, u.etunimi, u.sukunimi)}
                          >
                            Resetoi salasana
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <div className={styles.pagination}>
              <span style={{ marginRight: '10px' }}>
                <IconButton direction="left" onClick={handlePreviousPage} disabled={currentPage === 1} />
              </span>
              <span className={styles.pageCounter}>{currentPage} / {totalPages || 1}</span>
               <span style={{ marginLeft: '10px' }}>
                <IconButton direction="right" onClick={handleNextPage} disabled={currentPage === totalPages || totalPages === 0} /></span>
            </div>
          </div>
        )}

        {/* INVITES TAB */}
        {tab === 'invites' && (
          <>
            {/* Suodatinpillerit */}
            <div className={styles.inviteFilters}>
              {[
                { key: 'pending', label: 'Odottaa' },
                { key: 'accepted', label: 'Hyväksytty' },
                { key: 'expired', label: 'Vanhentunut' },
                { key: 'all', label: 'Kaikki' },
              ].map(f => (
                <button
                  key={f.key}
                  className={`${styles.filterPill} ${inviteFilter === f.key ? styles.filterPillActive : ''}`}
                  onClick={() => setInviteFilter(f.key)}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <div className={styles.userlistCard}>
            <table className={styles.userTable}>
              <thead className={styles.tableHead}>
                <tr>
                  <th className={styles.colName}>Nimi</th>
                  <th className={styles.colEmailWider}>Sähköposti</th>
                  <th className={styles.colRoles}>Roolit</th>
                  <th className={styles.colState}>Tila</th>
                  <th className={styles.colLogin}>Lähetetty</th>
                  <th className={styles.colLogin}>Voimassa</th>
                  <th className={styles.colActions}>Toiminnot</th>
                </tr>
              </thead>
              <tbody className={styles.tableBody}>
                {invitesLoading && (<tr><td colSpan={7} className={styles.muted}>Ladataan…</td></tr>)}

                {!invitesLoading && currentInvites.map((iv) => {
                  const st = iv.status || 'pending';
                  const isPending = st === 'pending';
                  const isAccepted = st === 'accepted';
                  const isExpired = st === 'expired';

                  return (
                    <tr key={iv.id} className={styles.memberRow}>
                      <td>{iv.name || '-'}</td>
                      <td>{iv.email || '-'}</td>
                      <td>{formatRoles(iv.roles)}</td>
                      <td>
                        <span className={`${styles.statusBadge} ${
                          isPending ? styles.statusPending :
                          isAccepted ? styles.statusAccepted :
                          isExpired ? styles.statusExpired : styles.statusCancelled
                        }`}>
                          {isPending ? 'Odottaa' : isAccepted ? 'Hyväksytty' : isExpired ? 'Vanhentunut' : 'Peruttu'}
                        </span>
                      </td>
                      <td>{fmtDate(iv.createdAt)}</td>
                      <td>{fmtDate(iv.expiresAt)}</td>
                      <td>
                        <div className={styles.actions}>
                          {isPending && (
                            <>
                              <button className={styles.stateButton} onClick={() => handleResendInvite(iv.id)}>Lähetä uudelleen</button>
                              <button className={styles.deleteBtn} onClick={() => handleCancelInvite(iv.id)}>Poista kutsu</button>
                            </>
                          )}
                          {isExpired && (
                            <>
                              <button className={styles.stateButton} onClick={() => handleResendInvite(iv.id)}>Lähetä uudelleen</button>
                              <button className={styles.deleteBtn} onClick={() => handleCancelInvite(iv.id)}>Poista kutsu</button>
                            </>
                          )}
                          {isAccepted && (
                            <button
                              className={styles.stateButton}
                              onClick={() => iv.acceptedBy ? handleRowClick(iv.acceptedBy) : null}
                              disabled={!iv.acceptedBy}
                              title={!iv.acceptedBy ? 'Käyttäjätunnusta ei löydy' : 'Avaa käyttäjän tiedot'}
                            >
                              Käyttäjän tiedot
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}

                {!invitesLoading && filteredInvites.length === 0 && (
                  <tr><td colSpan={7} className={styles.muted}>
                    {inviteFilter === 'pending' ? 'Ei avoimia kutsuja.' : 'Ei kutsuja valitulla suodattimella.'}
                  </td></tr>
                )}
              </tbody>
            </table>

            {/* Kutsujen sivutus */}
            <div className={styles.pagination}>
              <span style={{ marginRight: '10px' }}>
                <IconButton direction="left" onClick={handleInvitePrevPage} disabled={invitePage === 1} />
              </span>
              <span className={styles.pageCounter}>{invitePage} / {totalInvitePages || 1}</span>
              <span style={{ marginLeft: '10px' }}>
                <IconButton
                  direction="right"
                  onClick={handleInviteNextPage}
                  disabled={invitePage === totalInvitePages || totalInvitePages === 0}
                />
              </span>
            </div>
            </div>
          </>
        )}

        {/* LUONTIMODAALI sivulla */}
        {showCreateModal && (
          <>
            <div className={styles.modalBackdrop} onClick={closeCreateModal} aria-hidden="true" />
            <div className={styles.modalRoot} role="dialog" aria-modal="true" aria-labelledby="create-modal-title" onClick={closeCreateModal}>
              <div className={styles.modalCard} onClick={(e) => e.stopPropagation()}>
                <button className={styles.modalClose} aria-label="Sulje" onClick={closeCreateModal}>×</button>

                <h3 id="create-modal-title" className={styles.modalTitleLarge}>
                  {createMode === 'user' ? 'Uusi käyttäjä' : 'Luo kutsu'}
                </h3>

                {/* Etunimi + Sukunimi */}
                <div className={styles.formRowGrid}>
                  <div className={styles.formCol}>
                    <label className={styles.label}>Etunimi</label>
                    <input
                      ref={firstNameRef}
                      className={`${styles.input} ${showError('firstName') ? styles.inputError : ''}`}
                      placeholder="Etunimi"
                      value={createForm.firstName}
                      onChange={(e) => onChangeField('firstName', e.target.value)}
                      onBlur={() => onBlurField('firstName')}
                      aria-invalid={!!formErrors.firstName}
                    />
                    {showError('firstName') && <div className={styles.fieldError}>{formErrors.firstName}</div>}
                  </div>
                  <div className={styles.formCol}>
                    <label className={styles.label}>Sukunimi</label>
                    <input
                      ref={lastNameRef}
                      className={`${styles.input} ${showError('lastName') ? styles.inputError : ''}`}
                      placeholder="Sukunimi"
                      value={createForm.lastName}
                      onChange={(e) => onChangeField('lastName', e.target.value)}
                      onBlur={() => onBlurField('lastName')}
                      aria-invalid={!!formErrors.lastName}
                    />
                    {showError('lastName') && <div className={styles.fieldError}>{formErrors.lastName}</div>}
                  </div>
                </div>

                {/* Sähköposti */}
                <div className={styles.formRow}>
                  <label className={styles.label}>Sähköposti</label>
                  <input
                    ref={emailRef}
                    className={`${styles.input} ${showError('email') ? styles.inputError : ''}`}
                    placeholder="Sähköposti"
                    value={createForm.email}
                    onChange={(e) => onChangeField('email', e.target.value)}
                    onBlur={() => onBlurField('email')}
                    aria-invalid={!!formErrors.email}
                  />
                  {showError('email') && <div className={styles.fieldError}>{formErrors.email}</div>}
                </div>

                {/* Salasana (vain user) */}
                {createMode === 'user' && (
                  <div className={styles.formRow}>
                    <label className={styles.label}>Salasana</label>
                    <input
                      ref={passwordRef}
                      className={`${styles.input} ${showError('password') ? styles.inputError : ''}`}
                      type="password"
                      placeholder="Salasana"
                      value={createForm.password}
                      onChange={(e) => onChangeField('password', e.target.value)}
                      onBlur={() => onBlurField('password')}
                      aria-invalid={!!formErrors.password}
                    />
                    {showError('password') && <div className={styles.fieldError}>{formErrors.password}</div>}
                  </div>
                )}

                {/* Roolit – checkboxit */}
                <div className={styles.formRow}>
                  <label className={styles.label}>Roolit</label>
                  <div ref={rolesGroupRef} className={styles.roleGrid} aria-invalid={!!formErrors.roles}>
                    {['asukas', 'osakas', 'hallitus'].map((r) => {
                      const selected = createForm.roles.includes(r);
                      return (
                        <label
                          key={r}
                          className={`${styles.roleOption} ${selected ? styles.roleOptionSelected : ''} ${showError('roles') ? styles.roleOptionError : ''}`}
                        >
                          <input
                            type="checkbox"
                            className={styles.roleCheckbox}
                            checked={selected}
                            onChange={() => toggleRole(r)}
                          />
                          <span className={styles.roleText}>{ROLE_LABEL[r]}</span>
                        </label>
                      );
                    })}
                  </div>
                  {showError('roles') && <div className={styles.fieldError}>{formErrors.roles}</div>}
                </div>

                {/* Footer */}
                <div className={styles.modalFooterLeftAligned}>
                  <button className={styles.primaryBtn} disabled={disableSave} onClick={onSubmitCreate}>
                    {createMode === 'user' ? 'Luo käyttäjä' : 'Luo kutsu'}
                  </button>
                  <button className={styles.cancelBtnTight} onClick={closeCreateModal}>
                    Peruuta
                  </button>
                </div>

                {toast && <div className={styles.toast}>{toast}</div>}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
